import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  LayoutDashboard,
  Kanban,
  Users,
  BarChart3,
  Wifi,
  MoreHorizontal,
  X,
  ArrowLeft,
  Sparkles,
  History,
  FileText,
} from 'lucide-react';
import clsx from 'clsx';

interface WaMenuItem {
  id: string;
  label: string;
  shortLabel: string;
  path: string;
  icon: React.ElementType;
}

const menuItems: WaMenuItem[] = [
  { id: 'chat', label: 'Conversas', shortLabel: 'Chat', path: '/whatsapp', icon: MessageCircle },
  {
    id: 'agora',
    label: 'Agora',
    shortLabel: 'Agora',
    path: '/whatsapp/dashboard',
    icon: LayoutDashboard,
  },
  { id: 'kanban', label: 'Kanban', shortLabel: 'Kanban', path: '/whatsapp/kanban', icon: Kanban },
  {
    id: 'dashboard',
    label: 'Análise Inteligente',
    shortLabel: 'Análise',
    path: '/whatsapp/relatorios',
    icon: BarChart3,
  },
  { id: 'ia', label: 'IA', shortLabel: 'IA', path: '/whatsapp/ai-insights', icon: Sparkles },
  {
    id: 'historico',
    label: 'Historico',
    shortLabel: 'Historico',
    path: '/whatsapp/historico',
    icon: History,
  },
  {
    id: 'contatos',
    label: 'Contatos',
    shortLabel: 'Contatos',
    path: '/whatsapp/clientes',
    icon: Users,
  },
  {
    id: 'templates',
    label: 'Templates',
    shortLabel: 'Templates',
    path: '/whatsapp/templates',
    icon: FileText,
  },
  { id: 'conexao', label: 'Conexao', shortLabel: 'Conexao', path: '/whatsapp/conexao', icon: Wifi },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function WaLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const isChatView = location.pathname === '/whatsapp';

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/whatsapp') return location.pathname === '/whatsapp';
    return location.pathname.startsWith(path);
  };

  // Desktop/Tablet: Header já cuida da navegação, WaLayout é só wrapper
  if (!isMobile) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-gray-50">
        <main className={clsx('flex-1 overflow-hidden', !isChatView && 'overflow-y-auto')}>
          {isChatView ? (
            <div className="h-full">{children}</div>
          ) : (
            <div className="p-4 md:p-6">{children}</div>
          )}
        </main>
      </div>
    );
  }

  // Mobile: bottom tab bar estilo app nativo
  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Conteúdo */}
      <main className={clsx('flex-1 overflow-hidden', !isChatView && 'overflow-y-auto')}>
        {isChatView ? (
          <div className="h-full" style={{ paddingBottom: '56px' }}>
            {children}
          </div>
        ) : (
          <div className="p-4 pb-20">{children}</div>
        )}
      </main>

      {/* Bottom tab bar nativo */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="mx-auto flex h-14 max-w-lg items-center justify-around">
          {menuItems.slice(0, 4).map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => navigate(item.path)}
                className={clsx(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 py-1 transition-colors',
                  active ? 'text-green-600' : 'text-gray-400',
                )}
              >
                <Icon size={22} strokeWidth={active ? 2.2 : 1.5} />
                <span
                  className={clsx(
                    'text-[10px] leading-none',
                    active ? 'font-semibold' : 'font-medium',
                  )}
                >
                  {item.shortLabel}
                </span>
              </button>
            );
          })}
          {/* Mais */}
          <button
            type="button"
            onClick={() => setMoreOpen(!moreOpen)}
            className={clsx(
              'flex flex-1 flex-col items-center justify-center gap-0.5 py-1 transition-colors',
              moreOpen ? 'text-green-600' : 'text-gray-400',
            )}
          >
            <MoreHorizontal size={22} strokeWidth={1.5} />
            <span className="text-[10px] font-medium leading-none">Mais</span>
          </button>
        </div>

        {/* Sheet "Mais" */}
        {moreOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/30"
              role="button"
              tabIndex={0}
              aria-label="Fechar menu"
              onClick={() => setMoreOpen(false)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setMoreOpen(false);
              }}
            />
            {/* Sheet */}
            <div
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-white shadow-2xl"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              {/* Handle */}
              <div className="flex justify-center pb-1 pt-3">
                <div className="h-1 w-10 rounded-full bg-gray-300" />
              </div>
              <div className="flex items-center justify-between px-5 pb-2">
                <span className="text-base font-semibold text-gray-800">Menu</span>
                <button type="button" onClick={() => setMoreOpen(false)} className="p-1">
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-1 px-4 pb-4">
                {menuItems.slice(4).map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => navigate(item.path)}
                      className={clsx(
                        'flex w-full items-center gap-3 rounded-xl px-4 py-3.5 transition-colors',
                        active ? 'bg-green-50 text-green-700' : 'text-gray-600 active:bg-gray-50',
                      )}
                    >
                      <Icon size={20} className={active ? 'text-green-600' : 'text-gray-400'} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-gray-400 active:bg-gray-50"
                  >
                    <ArrowLeft size={20} />
                    <span className="text-sm font-medium">Voltar ao Indicadores</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>
    </div>
  );
}
